import {
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { CSSProperties, forwardRef, useImperativeHandle, useState } from "react";
import {
  Create,
  FileInput,
  Identifier,
  RecordContextProvider,
  SaveButton,
  SimpleForm,
  useGetOne,
  useRecordContext,
} from "react-admin";
import ImageIcon from "@mui/icons-material/Image";
import { useQueryClient } from "react-query";
import { DateField } from "../fields/DateField";
import { useThreadId } from "../hooks/useThreadId.js";
import { Spinner } from "../misc/Spinner";
import { Document } from "../model/Document";
import type { Thread } from "../model/Thread.js";
import { determineMimeType } from "../utils/determineMimeType";
import { getFileNameExtension } from "../utils/getFileNameExtension";
import { gcs } from "../utils/storage.js";
import { formatFileSize } from "../utils/string";
import { Row } from "../misc/Row";
import { AttachmentIcon } from "../icons/AttachmentIcon";
import { openDownloadWindow } from "../utils/openDownloadWindow";
import { truncateFileName } from "../utils/truncateFileName";

export const ThreadDocumentsList = forwardRef(({ style }: { style?: CSSProperties }, ref) => {
  const threadId = useThreadId();
  return (
    <div style={style}>
      <_ThreadDocumentsList threadId={threadId} ref={ref} />
    </div>
  );
});

const _ThreadDocumentsList = forwardRef(({ threadId }: { threadId: string }, ref) => {
  const { data: thread, isLoading } = useGetOne<Thread>("threads", { id: threadId });
  const postsWithAttachments = thread?.sortedPosts ?? [];
  const attachments = postsWithAttachments.flatMap((post) => post.attachments ?? []);
  const documents = attachments.map((attachment) => {
    return {
      id: attachment.id,
      path: attachment.path,
      fileName: attachment.name,
      contentType: attachment.mimeType,
      createdAt: attachment.createdAt,
      getDownloadUrl: () => gcs.getDownloadUrl(attachment.path),
    };
  });
  const [state, setState] = useState<"show button" | "show form">("show button");

  useImperativeHandle(ref, function () {
    return {
      showForm: () => setState("show form"),
    };
  });

  const form = state === "show form" && (
    <_UploadDocumentForm recordId={threadId} close={() => setState("show button")} />
  );

  if (isLoading) {
    return <Spinner style={{ margin: "20px" }} />;
  }

  if (!documents.length) {
    return (
      form || (
        <>
          <Typography
            variant="body2"
            sx={{
              marginBottom: "30px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              color: "text.secondary",
            }}
          >
            Keine Dokumente vorhanden.
          </Typography>
        </>
      )
    );
  }
  return (
    <>
      {form}
      <List sx={{ p: 0 }}>
        {documents.map((document) => (
          <RecordContextProvider key={document.id} value={document}>
            <_DocumentsListItem />
          </RecordContextProvider>
        ))}
      </List>
    </>
  );
});

function _DocumentsListItem() {
  const document = useRecordContext<Document>();
  const fileName = document.fileName;

  return (
    <ListItem
      disablePadding
      sx={{
        mt: 0,
        mb: "10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        opacity: document.deleted ? 0.6 : 1,
      }}
    >
      <ListItemButton
        sx={{ padding: 0, overflowWrap: "break-word", alignItems: "baseline", width: "100%" }}
        onClick={async () => {
          const documentWindow = openDownloadWindow();
          try {
            const downloadUrl = await document.getDownloadUrl();
            documentWindow.navigate(downloadUrl);
          } catch (error) {
            documentWindow.close();
            handleError("Fehler beim Öffnen des Dokuments.", error);
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: "30px", mt: "4px" }}>
          <Row
            sx={{
              alignItems: "center",
              justifyContent: "center",
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              backgroundColor: "action.hover",
            }}
          >
            {document.contentType.startsWith("image/") ? (
              <ImageIcon sx={{ fontSize: "16px" }} />
            ) : (
              <AttachmentIcon sx={{ "&.MuiSvgIcon-root": { width: "7px", height: "12px" } }} />
            )}
          </Row>
        </ListItemIcon>
        <ListItemText
          sx={{
            "& .MuiTypography-root": {
              fontSize: "14px",
              lineHeight: 1.29,
              fontWeight: "normal",
              color: "text.primary",
              textDecoration: document.deleted ? "line-through" : "none",
            },
          }}
          primary={
            fileName.length >= 24 ? (
              <Tooltip title={fileName}>
                <span>{truncateFileName(fileName, 27)}</span>
              </Tooltip>
            ) : (
              fileName
            )
          }
          secondary={
            <DateField
              sx={{ "&.MuiTypography-root": { color: "text.secondary", fontSize: "12px" } }}
              source="createdAt"
              showTime
            />
          }
        />
      </ListItemButton>
    </ListItem>
  );
}

function _UploadDocumentForm({ recordId, close }: { recordId: Identifier; close: () => void }) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [files, setFiles_] = useState<Array<File>>([]);
  const [mimeTypes, setMimeTypes] = useState<Array<string>>([]);
  const setFiles = async (newFiles: Array<File>) => {
    const newMimeTypes = await Promise.all(newFiles.map(determineMimeType));
    for (let i = 0; i < newFiles.length; i++) {
      const extension = getFileNameExtension(newFiles[i].name);
      if (extension === "pdf" && newMimeTypes[i] !== "application/pdf") {
        newMimeTypes[i] = ""; // <-- this will render the name of the invalid pdf file in red color
      }
    }
    setFiles_(files.concat(newFiles));
    setMimeTypes(mimeTypes.concat(newMimeTypes));
  };
  return (
    <Create
      resource="threadDocuments"
      className="upload-document"
      sx={{
        marginTop: 0,
        marginBottom: "8px", // ... not 16px because the <ul> below this form already has a top padding of 8px
        border: "1px solid",
        borderColor: "divider",
      }}
      record={{ recordId }}
      mutationOptions={{
        onSuccess: async () => {
          await queryClient.invalidateQueries(["threads"]);
          close();
        },
      }}
    >
      <SimpleForm
        sx={{ padding: "6px" }}
        toolbar={
          <div style={{ padding: "0 6px 6px 6px", display: "flex", justifyContent: "flex-end" }}>
            <Button variant="outlined" size="small" onClick={close} style={{ marginRight: "6px" }}>
              Abbrechen
            </Button>
            <SaveButton icon={<></>} />
          </div>
        }
      >
        <FileInput
          label={false}
          name="files"
          source="files"
          multiple
          options={{ onDrop: setFiles }}
          placeholder={
            <>
              {files.length === 0 && (
                <Typography component="p" sx={{ color: "text.disabled" }}>
                  Datei hier hineinziehen oder
                  <br />
                  hier klicken, um Datei auszuwählen ...
                </Typography>
              )}
              {files.map((file: File, index) => (
                <p
                  key={file.name}
                  style={{
                    marginTop: index === 0 ? "16px" : 0,
                    marginBottom: index === files.length - 1 ? "16px" : 0,
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    color: file.size === 0 || !mimeTypes[index] ? theme.palette.error.main : theme.palette.text.primary,
                  }}
                >
                  {file.name} ({formatFileSize(file.size)})
                </p>
              ))}
            </>
          }
        />
      </SimpleForm>
    </Create>
  );
}
